import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button, Modal } from 'antd';
import { pageSpecificTours } from 'data/tourSteps';

import { selectGuideCompletedPageTours } from 'features/auth/authSelectors';
import {
  setIsOpenApplicationTour,
  setIsOpenJobTour,
  setSteps
} from 'features/tour/tourSlice';

import { useAppDispatch, useAppSelector } from 'hooks/useTypedRedux';

import { closeSidebar } from 'features/sidebar/sidebarSlice';

import { GuideType, HowItWorksActionTypes } from 'constants/enums';
import { APPLICATIONS, JOBS } from 'constants/path';

import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as Logo } from 'assets/icons/logo-blue.svg';

import ActionButton from '../../ActionButton/ActionButton';
import styles from './HowItWorksModal.module.scss';

interface HowItWorksModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const HowItWorksModal = ({ isOpen, onClose }: HowItWorksModalProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const guideCompletedPageTours = useAppSelector(selectGuideCompletedPageTours);

  const handleActionButtonClick = (type: HowItWorksActionTypes) => () => {
    dispatch(closeSidebar());
    
    switch (type) {
      case HowItWorksActionTypes.StartJobsTour:
        dispatch(setIsOpenJobTour(true));
        dispatch(setSteps(pageSpecificTours[GuideType.Jobs]));
        navigate(JOBS);
        break;
      case HowItWorksActionTypes.StartApplicationsTour:
        dispatch(setIsOpenApplicationTour(true));
        dispatch(setSteps(pageSpecificTours[GuideType.Applications]));
        navigate(APPLICATIONS);
        break;
    }

    onClose();
  };

  return (
    <Modal
      className={styles.modal}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      closeIcon={
        <span className={styles.closeButton}>
          <CloseIcon />
        </span>
      }
      destroyOnHidden
    >
      <div className={styles.container}>
        <div className={styles.logoWrapper}>
          <Logo />
        </div>

        <div className={styles.content}>
          <div className={styles.header}>
            <div className={styles.title}>
              {t('tour.howItWorksModal.title')}
            </div>
            <div className={styles.subtitle}>
              {t('tour.howItWorksModal.subtitle')}
            </div>
          </div>

          <div className={styles.actionButtonsWrapper}>
            <ActionButton
              title={t('tour.button.jobsManagement')}
              onClick={handleActionButtonClick(
                HowItWorksActionTypes.StartJobsTour
              )}
              isCompleted={guideCompletedPageTours.jobsGuideCompleted}
            />
            <ActionButton
              title={t('tour.button.applicationsManagement')}
              onClick={handleActionButtonClick(
                HowItWorksActionTypes.StartApplicationsTour
              )}
              isCompleted={guideCompletedPageTours.applicationsGuideCompleted}
            />
          </div>

          <Button type="primary" className={styles.button} onClick={onClose}>
            {t('tour.howItWorksModal.buttonClose')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default HowItWorksModal;
